import { createRoot } from "react-dom/client";
import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PrintableInquiry from "../components/Pdf/PrintableInquiry";

/**
 * generateInquiryPdf
 * - profile: object
 * - items: array
 * - filename: string (optional)
 * - onProgress: function(percent:number, pageIndex:number, total:number)
 */
export async function generateInquiryPdf({ profile, items = [], filename, onProgress }) {
  const PAGE_WIDTH_MM = 210;
  const PAGE_HEIGHT_MM = 297;

  const A4_PX_W = 794;
  const A4_PX_H = 1123;

  const chunkSize = 3;
  const itemChunks = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    itemChunks.push(items.slice(i, i + chunkSize));
  }

  const pages = [{ type: "profile", profile }, ...itemChunks.map((chunk, i) => ({ type: "items", items: chunk }))];
  const totalPages = pages.length;

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.style.top = "0px";
  container.style.width = `${A4_PX_W}px`;
  container.style.height = `${A4_PX_H}px`;
  container.style.overflow = "hidden";
  document.body.appendChild(container);

  const root = createRoot(container);

  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  try {
    let pageIndex = 0;
    let globalItemIndex = 0;
    for (const p of pages) {
      pageIndex += 1;
      // Build pageItems using a simple loop instead of .map with inline function to avoid 'no-loop-func' ESLint
      const pageItems = [];
      if (p.items && p.items.length) {
        for (const it of p.items) {
          globalItemIndex += 1;
          pageItems.push({ ...it, _globalIndex: globalItemIndex });
        }
      }

      const props = {
        type: p.type,
        profile: p.profile,
        items: pageItems,
        pageIndex,
        totalPages,
      };
      root.render(React.createElement(PrintableInquiry, props));

      // Allow browser to paint and let logo load
      // small delay to ensure fonts/images have loaded
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(container.firstChild, {
        scale: 1.6,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      if (pageIndex === 1) {
        doc.addImage(imgData, "PNG", 0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM);
      } else {
        doc.addPage();
        doc.addImage(imgData, "PNG", 0, 0, PAGE_WIDTH_MM, PAGE_HEIGHT_MM);
      }

      if (typeof onProgress === "function") {
        const percent = Math.round((pageIndex / totalPages) * 100);
        onProgress(percent, pageIndex, totalPages);
      }

      // free canvas memory
      // ensure cleanup
      // @ts-ignore
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    }

    // set PDF metadata/title
    const namePart = (profile?.companyName || profile?.fullName || "inquiry")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
    const dt = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const finalName = filename || `inquiry-${namePart || "inquiry"}-${dt}.pdf`;

    doc.setProperties({ title: finalName.replace(/\.pdf$/i, "") });
    doc.save(finalName);
  } catch (err) {
    console.error("PDF generation failed", err);
    throw err;
  } finally {
    try {
      root.unmount();
    } catch (e) { /* ignore */ }
    if (container && container.parentNode) container.parentNode.removeChild(container);
  }
}